import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, List, ListItemButton,
  ListItemText, Paper, Stack, TextField, Typography,
} from '@mui/material';
import reviewQueueService, {
  EvidenceItem, PossibleMatch, ResolvePlan,
} from '../../../services/reviewQueueService';
import wineNameService from '../../../services/wineNameService';

/**
 * SIMPLE-WINE.3 — THE RESOLVE VIEW.
 *
 * A provisional queue entry is a question: "what wine is this?" It has exactly
 * three answers, and this screen is built around them rather than around the
 * records involved:
 *
 *   A — it is one we already have   → match onto the canonical name
 *   B — it is a new wine            → fill in producer and region
 *   C — it is junk                  → reject
 *
 * The operator's typed text sits at the top throughout, because every one of
 * the three is a judgement about that text.
 */

const ResolveProvisional: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [typedText, setTypedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // EVIDENCE.1a — what this record was made from. Evidence is what you
  // consult BEFORE deciding, so it renders above the three answers.
  const [evidence, setEvidence] = useState<EvidenceItem[]>([]);
  const [suggestions, setSuggestions] = useState<PossibleMatch[]>([]);
  const [results, setResults] = useState<PossibleMatch[] | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  // The term that produced `results`. Without it the view cannot tell
  // "nothing matched what you asked for" from "you have not asked yet",
  // and it showed the first of those for both.
  const [searchedFor, setSearchedFor] = useState<string | null>(null);
  // Typing fires searches faster than they come back. Without a sequence
  // guard a slow early response can land after a fast later one and put
  // stale rows under a newer query.
  const searchSeq = useRef(0);

  const [selected, setSelected] = useState<PossibleMatch | null>(null);
  const [plan, setPlan] = useState<ResolvePlan | null>(null);
  const [planError, setPlanError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<{ approvable: string[] } | null>(null);
  const [approving, setApproving] = useState(false);

  // Path A is preselected when the queue sent the reviewer here from a
  // suggestion, so the flow continues where it left off rather than restarting.
  const preselectId = searchParams.get('target') ?? '';

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [record, matches, items] = await Promise.all([
          wineNameService.getById(id),
          reviewQueueService.possibleMatches(id).catch(() => [] as PossibleMatch[]),
          // Quiet failure: evidence is context, and losing it must not cost
          // the reviewer the screen.
          reviewQueueService.evidenceItems(id).catch(() => [] as EvidenceItem[]),
        ]);
        if (cancelled) return;
        setTypedText(record.displayName ?? '');
        setSuggestions(matches);
        setEvidence(items);
        if (preselectId) {
          const found = matches.find((m) => m._id === preselectId);
          if (found) setSelected(found);
        }
      } catch {
        if (!cancelled) setLoadError('Could not load this wine name. Refresh to try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, preselectId]);

  // The preview is fetched the moment a candidate is chosen — the reviewer
  // should see the consequences before the confirm button, not after it.
  useEffect(() => {
    if (!selected || !id) { setPlan(null); return undefined; }
    let cancelled = false;
    setPlanError('');
    (async () => {
      try {
        const p = await reviewQueueService.resolvePreview(id, selected._id);
        if (!cancelled) setPlan(p);
      } catch (err: any) {
        if (!cancelled) {
          setPlan(null);
          setPlanError(err?.response?.data?.message || 'Could not work out what this would do.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selected, id]);

  const runSearch = useCallback(async (term?: string) => {
    if (!id) return;
    const q = (typeof term === 'string' ? term : query).trim();
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    setSearching(true);
    try {
      const rows = await reviewQueueService.candidates(id, q);
      if (seq !== searchSeq.current) return; // superseded by a later search
      setResults(rows);
      setSearchedFor(q);
    } catch {
      if (seq !== searchSeq.current) return;
      setResults([]);
      setSearchedFor(q);
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }, [id, query]);

  // Search as the reviewer types. The old view only searched on an explicit
  // button press, so typing "Cha" left the previous state on screen -- and
  // that state said "No canonical wine name matches". Every keystroke looked
  // like a search returning nothing.
  //
  // Two letters is the server's own minimum: below it the endpoint ignores
  // the term and answers with an alphabetical page of the whole catalogue,
  // which reads as a broken search rather than a short one.
  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      searchSeq.current += 1; // abandon anything in flight
      setResults(null);
      setSearchedFor(null);
      setSearching(false);
      return undefined;
    }
    if (q.length < 2) return undefined;
    const timer = setTimeout(() => { runSearch(q); }, 300);
    return () => clearTimeout(timer);
  }, [query, runSearch]);

  const handleConfirm = useCallback(async () => {
    if (!selected || !id) return;
    setConfirming(true);
    setPlanError('');
    try {
      // Report the list as the reviewer saw it, in the order they saw it.
      const shownNow = (results ?? suggestions).map((m, i) => ({
        wineNameId: m._id,
        overlap: typeof m.overlap === 'number' ? m.overlap : 0,
        rank: i,
      }));
      const res = await reviewQueueService.resolve(id, selected._id, shownNow);
      setDone({ approvable: res.approvable ?? [] });
    } catch (err: any) {
      setPlanError(
        err?.response?.data?.message || 'Could not resolve. Nothing was changed.',
      );
    } finally {
      setConfirming(false);
    }
  }, [selected, id, results, suggestions]);

  const handleApproveMoved = useCallback(async () => {
    if (!done) return;
    setApproving(true);
    try {
      for (const wineId of done.approvable) {
        await reviewQueueService.approve('wine', wineId);
      }
      navigate('/admin/governance/review-queue');
    } catch {
      setPlanError('The wines moved, but approving them did not finish. They are in the queue.');
    } finally {
      setApproving(false);
    }
  }, [done, navigate]);

  const shown = useMemo<PossibleMatch[]>(
    () => (results ?? suggestions),
    [results, suggestions],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        What wine is this?
      </Typography>

      {/* The typed text, at the top of every path. Each of the three answers
          is a judgement about exactly these words. */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">Operator wrote</Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>“{typedText}”</Typography>

        {/* EVIDENCE.1a — quiet when there is none, exactly as EvidencePanel is
            on the record screens. Most provisionals have only typed text. */}
        {evidence.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Label photo{evidence.filter((e) => e.kind === 'photo').length === 1 ? '' : 's'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {evidence.filter((e) => e.kind === 'photo').map((e) => (
                e.purged ? (
                  <Box
                    key={e._id}
                    sx={{
                      width: 96, height: 96, borderRadius: 1,
                      border: 1, borderColor: 'divider', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', p: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" align="center">
                      photo removed
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    key={e._id}
                    component="a"
                    href={e.url ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open label photo taken by ${e.actorEmail ?? 'a venue user'}`}
                  >
                    <Box
                      component="img"
                      src={e.url ?? undefined}
                      alt="Label"
                      sx={{
                        width: 96, height: 96, objectFit: 'cover',
                        borderRadius: 1, border: 1, borderColor: 'divider',
                      }}
                    />
                  </Box>
                )
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {(() => {
                const first = evidence.find((e) => e.kind === 'photo');
                const when = first?.createdAt
                  ? new Date(first.createdAt).toLocaleDateString() : null;
                return [first?.actorEmail ? `Added by ${first.actorEmail}` : null, when]
                  .filter(Boolean).join(' · ');
              })()}
            </Typography>
          </Box>
        )}
      </Paper>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {planError && <Alert severity="warning" sx={{ mb: 2 }}>{planError}</Alert>}

      {done ? (
        <Alert
          severity="success"
          action={done.approvable.length > 0 ? (
            <Button
              size="small"
              disabled={approving}
              onClick={handleApproveMoved}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {approving
                ? 'Approving…'
                : `Approve ${done.approvable.length} moved vintage${done.approvable.length === 1 ? '' : 's'}`}
            </Button>
          ) : (
            <Button
              size="small"
              onClick={() => navigate('/admin/governance/review-queue')}
              sx={{ textTransform: 'none' }}
            >
              Back to the queue
            </Button>
          )}
        >
          Resolved. {done.approvable.length > 0
            ? 'The moved vintages now hang on a canonical name, so they can be approved.'
            : 'Nothing is left to approve.'}
        </Alert>
      ) : (
        <>
          {/* ── PATH A ─────────────────────────────────────────────────── */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            A · Match to an existing wine
          </Typography>

          {/* A placeholder, not a floating label. An outlined label lives in a
              gap cut into the field's own border, and when that notch and the
              label disagree the text is drawn straight through by the border
              -- which is what was reported here. A search box needs no
              floating label to be understood, and without one the failure is
              not possible. The accessible name is unchanged. */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search all wine names"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
              inputProps={{ 'aria-label': 'Search all wine names' }}
            />
            <Button
              variant="outlined"
              onClick={() => runSearch()}
              disabled={searching}
              sx={{ textTransform: 'none' }}
            >
              {searching ? 'Searching…' : 'Search'}
            </Button>
          </Stack>

          {searching && (
            <Typography variant="caption" color="text.secondary">Searching…</Typography>
          )}
          {!searching && results === null && suggestions.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Closest matches to what the operator wrote:
            </Typography>
          )}
          {!searching && results !== null && (
            <Typography variant="caption" color="text.secondary">
              {results.length} result{results.length === 1 ? '' : 's'} for “{searchedFor}”
            </Typography>
          )}

          {/* Three states, not two. Nothing on screen yet is not the same
              answer as nothing matched, and saying the second when the first
              is true tells the reviewer the catalogue is empty when they have
              simply not asked it anything. */}
          {shown.length === 0 ? (
            searching ? null : results !== null ? (
              <Alert severity="info" sx={{ my: 2 }}>
                No canonical wine name matches “{searchedFor}”. If this is a wine we
                do not have yet, use “Create new wine name” below.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                Type at least two letters to search the wine catalogue.
              </Typography>
            )
          ) : (
            <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, my: 1 }}>
              {shown.map((m, i) => (
                <React.Fragment key={m._id}>
                  {i > 0 && <Divider component="li" />}
                  <ListItemButton
                    selected={selected?._id === m._id}
                    onClick={() => setSelected(m)}
                  >
                    <ListItemText
                      primary={m.displayName}
                      secondary={[
                        m.producerName, m.regionName,
                        m.vintages.length > 0
                          ? `vintages: ${m.vintages.map((v) => v.displayVintage).join(', ')}`
                          : 'no vintages yet',
                      ].filter(Boolean).join(' · ')}
                    />
                    {m.overlap !== undefined && (
                      <Chip size="small" variant="outlined" label={`${Math.round(m.overlap * 100)}% match`} />
                    )}
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}

          {/* The confirm summary. Generated server-side by the same code that
              performs the action, so it cannot describe something else. */}
          {selected && plan && (
            <Paper elevation={0} sx={{ p: 2, my: 2, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Confirm — this will:
              </Typography>
              {plan.summary.map((line) => (
                <Typography key={line} variant="body2" sx={{ mb: 0.5 }}>• {line}</Typography>
              ))}
              <Button
                variant="contained"
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
                disabled={confirming}
                onClick={handleConfirm}
              >
                {confirming ? 'Resolving…' : `Match to “${selected.displayName}”`}
              </Button>
            </Paper>
          )}

          <Divider sx={{ my: 3 }} />

          {/* ── PATHS B AND C ──────────────────────────────────────────── */}
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            B · It is a wine we do not have yet
          </Typography>
          <Button
            variant="outlined"
            sx={{ textTransform: 'none', mb: 3 }}
            onClick={() => navigate(`/admin/wine-names/edit/${id}`)}
          >
            Create new wine name
          </Button>

          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            C · It is not a real wine
          </Typography>
          <Button
            color="error"
            variant="outlined"
            sx={{ textTransform: 'none' }}
            onClick={async () => {
              if (!window.confirm(`Reject “${typedText}”?`)) return;
              await reviewQueueService.reject('wine_name', id);
              navigate('/admin/governance/review-queue');
            }}
          >
            Reject
          </Button>
        </>
      )}
    </Box>
  );
};

export default ResolveProvisional;
